/**
 * Bildirim (toast).
 *
 * ── NEDEN VAR ────────────────────────────────────────────────────────────────
 * Bir islem basarili oldugunda kullaniciya SOYLEMEK zorundayiz. Sessiz basari,
 * kullanicinin butona tekrar basmasina yol acar — ve bazi islemler (kredi
 * duzeltme, yetki verme) iki kez calismamali.
 *
 * ── ERISILEBILIRLIK ──────────────────────────────────────────────────────────
 * Bolge `aria-live="polite"`: ekran okuyucu kullanicinin sozunu KESMEDEN
 * bildirimi okur. Hata bildirimleri `assertive` — onlar kesmeli.
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import './toast.css';

type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Hata bildirimi daha uzun durur: okunmasi ve anlasilmasi gerekir. */
const DURATION: Record<ToastTone, number> = { success: 3500, info: 4000, error: 7000 };

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((tone: ToastTone, message: string) => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, tone, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, DURATION[tone]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider
      value={{
        success: (m) => push('success', m),
        error: (m) => push('error', m),
        info: (m) => push('info', m),
      }}
    >
      {children}

      <div className="toasts" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast--${t.tone}`}
            role={t.tone === 'error' ? 'alert' : 'status'}
          >
            <span className="toast__text">{t.message}</span>
            <button
              type="button"
              className="toast__close"
              onClick={() => dismiss(t.id)}
              aria-label="Bildirimi kapat"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast yalnızca ToastProvider içinde kullanılabilir.');
  return ctx;
}
